import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Button, CircularProgress, Stack, ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import { Refresh, FileDownload, Assessment } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { COUNTRY_LIST } from '../../utils/dialCountry';

const toLocalInput = (d: Date) => {
  const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
};
const nowLocal = () => toLocalInput(new Date());
const hoursAgoLocal = (h: number) => toLocalInput(new Date(Date.now() - h * 3600e3));
const startOfTodayLocal = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return toLocalInput(d); };

const QUICK_RANGES = [
  { v: 1, l: 'Last 1 hour' }, { v: 3, l: 'Last 3 hours' }, { v: 6, l: 'Last 6 hours' },
  { v: 12, l: 'Last 12 hours' }, { v: 21, l: 'Last 21 hours' }, { v: 24, l: 'Last 24 hours' },
  { v: 48, l: 'Last 48 hours' }, { v: 72, l: 'Last 72 hours' }, { v: 168, l: 'Last 7 days' },
];

type Group = 'customer' | 'trunk' | 'country';
const META: Record<Group, { title: string; firstCol: string }> = {
  customer: { title: 'Customer Summary', firstCol: 'Account' },
  trunk:    { title: 'Trunk Summary',    firstCol: 'Trunk' },
  country:  { title: 'Country Report',   firstCol: 'Destination' },
};

export const SummaryReportPage: React.FC<{ group: Group }> = ({ group }) => {
  const { enqueueSnackbar } = useSnackbar();
  const meta = META[group];
  const [rows, setRows] = useState<any[]>([]);
  const [totals, setTotals] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [timeUnit, setTimeUnit] = useState<'sec' | 'min'>('min');
  const [f, setF] = useState({ tenant_id: '', from: startOfTodayLocal(), to: nowLocal(), direction: 'outbound', country_code: '' });
  const [quick, setQuick] = useState<number | ''>('');
  const applyQuick = (h: number | '') => {
    setQuick(h);
    if (h !== '') setF(prev => ({ ...prev, from: hoursAgoLocal(h as number), to: nowLocal() }));
  };

  useEffect(() => {
    api.get('/tenants?limit=500').then(r => setCustomers(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { group };
      if (f.tenant_id) params.tenant_id = f.tenant_id;
      if (f.from) params.from = new Date(f.from).toISOString();
      if (f.to) params.to = new Date(f.to).toISOString();
      if (f.direction) params.direction = f.direction;
      if (f.country_code) params.country_code = f.country_code;
      const { data } = await api.get('/cdr/report/summary?' + new URLSearchParams(params));
      setRows(data.data || []);
      setTotals(data.totals || {});
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Failed to load report', { variant: 'error' });
    } finally { setLoading(false); }
  }, [group, f, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const fmtDur = (s: number) => {
    s = Number(s) || 0;
    if (timeUnit === 'min') return (s / 60).toFixed(2);
    const m = Math.floor(s / 60), ss = s % 60;
    return `${m}:${String(ss).padStart(2, '0')}`;
  };
  const money = (n: any) => (parseFloat(n) || 0).toFixed(4);
  const num = (n: any) => Number(n || 0).toLocaleString();

  const cols = useMemo(() => ([
    { k: 'attempted',    l: 'Attempted Calls', fmt: (r: any) => num(r.attempted),    align: 'right' as const },
    { k: 'completed',    l: 'Completed Calls', fmt: (r: any) => num(r.completed),    align: 'right' as const },
    { k: 'duration_sec', l: 'Duration',        fmt: (r: any) => fmtDur(r.duration_sec), align: 'right' as const },
    { k: 'billable_sec', l: 'Billable',        fmt: (r: any) => fmtDur(r.billable_sec), align: 'right' as const },
    { k: 'asr',          l: 'ASR *',           fmt: (r: any) => (parseFloat(r.asr) || 0).toFixed(2), align: 'right' as const },
    { k: 'acd_sec',      l: 'ACD *',           fmt: (r: any) => fmtDur(r.acd_sec), align: 'right' as const },
    { k: 'mcd_sec',      l: 'MCD *',           fmt: (r: any) => fmtDur(r.mcd_sec), align: 'right' as const },
    { k: 'debit',        l: 'Debit ($)',       fmt: (r: any) => money(r.debit), align: 'right' as const },
    { k: 'cost',         l: 'Cost ($)',        fmt: (r: any) => money(r.cost), align: 'right' as const },
    { k: 'profit',       l: 'Profit ($)',      fmt: (r: any) => money(r.profit), align: 'right' as const },
  ]), [timeUnit]);

  const exportCsv = () => {
    const head = [meta.firstCol, 'Attempted', 'Completed', 'Duration', 'Billable', 'ASR', 'ACD', 'MCD', 'Debit', 'Cost', 'Profit'];
    const line = (name: string, r: any) => [name, num(r.attempted), num(r.completed), fmtDur(r.duration_sec), fmtDur(r.billable_sec),
      (parseFloat(r.asr) || 0).toFixed(2), fmtDur(r.acd_sec), fmtDur(r.mcd_sec), money(r.debit), money(r.cost), money(r.profit)]
      .map(x => `"${String(x)}"`).join(',');
    const csv = [head.join(','), ...rows.map(r => line(r.name, r)), line('Grand Total', totals)].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${group}-summary-${f.from.replace(/[:T]/g, '-')}_${f.to.replace(/[:T]/g, '-')}.csv`; a.click();
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 2 }}>
        <Assessment color='primary' />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='h5' fontWeight={700}>{meta.title}</Typography>
          <Typography variant='caption' color='text.secondary'>Call Reports › {meta.title}</Typography>
        </Box>
        <ToggleButtonGroup size='small' exclusive value={timeUnit} onChange={(_, v) => v && setTimeUnit(v)}>
          <ToggleButton value='min'>Minutes</ToggleButton>
          <ToggleButton value='sec'>mm:ss</ToggleButton>
        </ToggleButtonGroup>
        <Button size='small' startIcon={<FileDownload />} onClick={exportCsv} disabled={!rows.length}>Export</Button>
        <Button size='small' variant='contained' startIcon={loading ? <CircularProgress size={14} /> : <Refresh />} onClick={load}>Run</Button>
      </Stack>

      <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size='small' label='Customer' value={f.tenant_id} onChange={e => setF({ ...f, tenant_id: e.target.value })} sx={{ minWidth: 220 }}>
            <MenuItem value=''>All customers</MenuItem>
            {customers.map((c: any) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Direction' value={f.direction} onChange={e => setF({ ...f, direction: e.target.value })} sx={{ minWidth: 130 }}>
            <MenuItem value=''>Both</MenuItem>
            <MenuItem value='outbound'>Outbound</MenuItem>
            <MenuItem value='inbound'>Inbound</MenuItem>
          </TextField>
          <TextField select size='small' label='Country' value={f.country_code} onChange={e => setF({ ...f, country_code: e.target.value })} sx={{ minWidth: 180 }}>
            <MenuItem value=''>All countries</MenuItem>
            {COUNTRY_LIST.map((c: any) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select size='small' label='Quick range' value={quick} onChange={e => applyQuick(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 150 }}>
            <MenuItem value=''>Custom</MenuItem>
            {QUICK_RANGES.map(r => <MenuItem key={r.v} value={r.v}>{r.l}</MenuItem>)}
          </TextField>
          <TextField size='small' type='datetime-local' label='From' InputLabelProps={{ shrink: true }} value={f.from} onChange={e => { setQuick(''); setF({ ...f, from: e.target.value }); }} />
          <TextField size='small' type='datetime-local' label='To' InputLabelProps={{ shrink: true }} value={f.to} onChange={e => { setQuick(''); setF({ ...f, to: e.target.value }); }} />
        </Box>
      </Paper>

      <TableContainer component={Paper} variant='outlined'>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{meta.firstCol}</TableCell>
              {cols.map(c => <TableCell key={c.k} align='right' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.l}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={cols.length + 1} align='center' sx={{ py: 4 }}><CircularProgress size={26} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={cols.length + 1} align='center' sx={{ py: 4, color: 'text.secondary' }}>No data for the selected filters</TableCell></TableRow>
            ) : rows.map((r, i) => (
              <TableRow key={i} hover>
                <TableCell><Typography variant='body2' fontWeight={600}>{r.name}</Typography></TableCell>
                {cols.map(c => <TableCell key={c.k} align='right'>{c.fmt(r)}</TableCell>)}
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>Grand Total</TableCell>
                {cols.map(c => <TableCell key={c.k} align='right'>{c.k === 'asr' ? (parseFloat(totals.asr) || 0).toFixed(2) : c.fmt(totals)}</TableCell>)}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant='caption' color='text.secondary' sx={{ mt: 1, display: 'block' }}>
        * ASR = Answer-Seizure Ratio · ACD = Average Call Duration · MCD = Max Call Duration
      </Typography>
    </Box>
  );
};
